/*  iceberg-ui  */
import { Box } from '@auviknetworks/iceberg-ui';

/*  MUI  */
import { Download } from '@mui/icons-material';
import { Button, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

/*  Shared  */
import CustomScript from 'application/Shared/Components/CustomScript/Containers/CustomScript';
import { FontSizes } from 'application/Shared/Constants';
import { Route } from 'application/Shared/Helpers';
import { Env } from 'application/Shared/Utils';

/* Hooks */
import { usePostSubscriberSite } from 'hooks/dataAccess/TenantIdentifier/tenantIdentifier';
import { useUserTheme } from 'hooks/dataAccess/User/userTheme';

/*  React  */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

/*  Components  */
import { PartnerIntegrationWizardWrapper } from './Components/PartnerIntegrationWizardWrapper';
import IntegrationSteps from './Components/Steps';

/* Constants */
import IntegrationScriptFilename from './Constants/IntegrationScriptsFilename.json';

const UnifiedAgentStep1 = ({
    clientId,
    disableClientSelect,
    disableSiteSelect,
    setClientId,
    setSite,
    site,
    children
}) => (
    <Box width="100%">
        <CustomScript
            clientId={clientId}
            disableClientSelect={disableClientSelect}
            disableSiteSelect={disableSiteSelect}
            forUnifiedAgent
            onboarding
            setClientId={setClientId}
            setSite={setSite}
            site={site}
        />
        {children}
    </Box>
);

const UnifiedAgentStep2 = ({ site, ...props }) => {
    const { t } = useTranslation();
    const userTheme = useUserTheme();
    const theme = useTheme();
    const { unifiedAgentScript } = IntegrationScriptFilename;

    const href = Env.get('REACT_APP_AUVIK_AGENT_URL');
    const regKeyHref = `https://${site.domainPrefix}.${Env.get(
        'REACT_APP_AUVIK_REGISTRATION_KEY'
    )}`;

    // Adding to trigger build
    const windows = [
        {
            component: (
                <Box
                    alignItems="center"
                    display="flex"
                    justifyContent="space-between"
                    width="100%"
                >
                    <Typography
                        style={{ lineHeight: '24px', marginBottom: '4px' }}
                    >
                        {t('Integrations.unifiedAgent.windows.step1')}
                    </Typography>
                    <a href={href}>
                        <Button
                            variant="contained"
                            color="primary"
                            style={{ marginLeft: '8px' }}
                            size="small"
                        >
                            <Download
                                style={{
                                    fontSize: FontSizes[2],
                                    marginRight: '4px'
                                }}
                            />
                            {t('Common.download')}
                        </Button>
                    </a>
                </Box>
            ),
            step: true
        },
        {
            component: (
                <Typography style={{ lineHeight: '24px', marginBottom: '4px' }}>
                    {t('Integrations.unifiedAgent.windows.step2')}
                </Typography>
            ),
            step: true
        },
        {
            component: (
                <Box>
                    <Typography
                        style={{ lineHeight: '24px', marginBottom: '4px' }}
                    >
                        {t('Integrations.unifiedAgent.windows.step3')}
                    </Typography>
                    <Box mt="8px">
                        <a
                            href={regKeyHref}
                            className="link--secondary"
                            target="_blank"
                            rel="noreferrer"
                        >
                            <span>{regKeyHref}</span>
                        </a>
                    </Box>
                </Box>
            ),
            step: true
        },
        {
            component: (
                <Typography style={{ lineHeight: '24px', marginBottom: '4px' }}>
                    {t('Integrations.unifiedAgent.windows.step4')}
                </Typography>
            ),
            step: true
        },
        {
            component: (
                <Typography style={{ lineHeight: '24px', marginBottom: '4px' }}>
                    {t('Integrations.unifiedAgent.windows.step5')}
                </Typography>
            ),
            copyValue: unifiedAgentScript,
            step: true
        }
    ];

    const mac = [
        {
            component: (
                <Typography style={{ lineHeight: '24px', marginBottom: '4px' }}>
                    {t('Integrations.unifiedAgent.mac.prestep')}
                </Typography>
            ),
            step: false
        },
        {
            component: (
                <Typography style={{ lineHeight: '24px', marginBottom: '4px' }}>
                    {t('Integrations.unifiedAgent.mac.step1')}
                </Typography>
            ),
            step: true
        },
        {
            component: (
                <Typography style={{ lineHeight: '24px', marginBottom: '4px' }}>
                    {t('Integrations.unifiedAgent.mac.step2')}
                </Typography>
            ),
            step: true
        },
        {
            component: (
                <Typography style={{ lineHeight: '24px', marginBottom: '4px' }}>
                    {t('Integrations.unifiedAgent.mac.step3')}
                </Typography>
            ),
            step: true
        }
    ];

    const renderWindows = () => (
        <Box
            data-testid="unifiedAgentRenderWindows"
            style={{
                backgroundColor: theme.palette.background.primary,
                padding: '24px 32px 24px 32px',
                width: '100%'
            }}
        >
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
            >
                <Box display="flex" alignItems="center">
                    <img
                        src={`/images/${userTheme}/integrations/windows_os.png`}
                        alt={t('Common.windowsOS.logo')}
                        style={{ height: '72px', width: '72px' }}
                    />
                    <Typography
                        variant="h3"
                        marginLeft="16px"
                        textTransform="none"
                    >
                        {t('Common.windowsOS')}
                    </Typography>
                </Box>
            </Box>
            <Box mt="40px">
                <IntegrationSteps steps={windows} />
            </Box>
        </Box>
    );

    // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
    const renderMac = () => (
        <Box
            data-testid="unifiedAgentRenderMac"
            style={{
                backgroundColor: theme.palette.background.primary,
                padding: '24px 32px 24px 32px',
                width: '100%'
            }}
        >
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
            >
                <Box display="flex" alignItems="center">
                    <img
                        src={`/images/${userTheme}/integrations/mac_os.png`}
                        alt={t('Common.macos.logo')}
                        style={{ height: '72px', width: '72px' }}
                    />
                    <Typography
                        variant="h3"
                        marginLeft="16px"
                        textTransform="none"
                    >
                        {t('Common.macOS')}
                    </Typography>
                </Box>
                {/* TODO: Add installer */}
                <Button>{t('Common.downloadInstaller')}</Button>
            </Box>
            <Box mt="40px">
                <IntegrationSteps steps={mac} />
            </Box>
        </Box>
    );
    return (
        <UnifiedAgentStep1 {...props}>
            <Box mt="24px">
                <Box>{renderWindows()}</Box>
                {/* <Box mt="24px">{renderMac()}</Box> */}
            </Box>
        </UnifiedAgentStep1>
    );
};

export const UnifiedAgent = ({ onboarding = false }) => {
    const { t } = useTranslation();
    const [clientId, setClientId] = useState('0');
    const [site, setSite] = useState('0');
    const { partnerId } = useParams();
    const basePartnerRoute = Route.buildBasePartnerRoute({
        partnerId
    });

    const { postSubscriberSite, saving } = usePostSubscriberSite({
        platformTenantId: clientId,
        type: 'ANM',
        value: site
    });

    const backToIntegrationsLink = `${basePartnerRoute}/integrations/partnerLevel`;

    const sharedData = {
        clientId,
        setClientId,
        setSite,
        site
    };

    const steps = [
        {
            buttonType: 'First',
            component: UnifiedAgentStep1,
            disableNext: clientId === '0' || site === '0' || !clientId || !site,
            labelText: t('Integrations.unifiedAgent.ChooseClientAndSite'),
            nextFn: async () => postSubscriberSite(),
            number: '1'
        },
        {
            buttonType: 'Done',
            component: UnifiedAgentStep2,
            doneLink: backToIntegrationsLink,
            labelText: t(
                'Integrations.unifiedAgent.ChooseTheAgentDeploymentProcess'
            ),
            number: '2',
            props: { disableClientSelect: true, disableSiteSelect: true }
        }
    ];

    return (
        <PartnerIntegrationWizardWrapper
            steps={steps}
            headerTitle={t('Integrations.agent.title')}
            onboarding={onboarding}
            sharedData={sharedData}
            cancelLink={backToIntegrationsLink}
            loading={saving}
        />
    );
};
